import { inject } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { CanActivateFn, Router, UrlTree } from '@angular/router';
import { combineLatest, Observable, of } from 'rxjs';
import { filter, map, take } from 'rxjs/operators';

import { UserState } from '@app/auth/user.state';
import { BootstrapSuffix } from '@app/common.model';
import { ProjectState, Status } from '@app/projects/project.state';
import { ORG_ID_PARAM, PROJECT_ID_PARAM } from '@app/shared/app-router.service';
import { ToastService } from '@app/toasts/toast.service';

/**
 * Guard to ensure:
 * 1. We load the requested project from the route or query param.
 * 2. If there's no project to load or the project is already loaded, we:
 *    - Return true if we're not waiting for secrets
 *    - Wait for secrets to load if waitForSecrets is true
 * 3. If there's an orgId mismatch in the query params, we fix it:
 *    - If an orgId param is present but doesn't match the loaded project → remove projectId
 *    - If there's no orgId param but the loaded project has one → add orgId
 * 4. On project load error:
 *    - Display error toast
 *    - Redirect to backTo route if specified, otherwise return true
 */
export const projectGuard = (
  param?: string,
  backTo?: string,
  waitForSecrets = false,
): CanActivateFn => {
  return (route, state): boolean | Observable<UrlTree | boolean> => {
    const projectState = inject(ProjectState);
    const userState = inject(UserState);
    const router = inject(Router);
    const toastService = inject(ToastService);
    const projectId = route.queryParams[PROJECT_ID_PARAM] ?? (param ? route.params[param] : null);

    if (!projectId || projectId === projectState.project()?.id) {
      if (!waitForSecrets || !projectState.secrets.isLoading()) {
        return true;
      }
    }

    projectState.loadProject$.next(projectId);

    const status$ = toObservable(projectState.status);
    const secretsLoading$ = waitForSecrets
      ? toObservable(projectState.secrets.isLoading)
      : of(false);

    return combineLatest([status$, secretsLoading$]).pipe(
      filter(([status, secretsLoading]) => status !== Status.Loading && !secretsLoading),
      take(1),
      map(([status]) => {
        if (status === Status.Error) {
          toastService.show({
            type: BootstrapSuffix.Danger,
            title: 'Error!',
            text: `Failed to load the project! (id: ${projectId})`,
          });
          return backTo ? router.parseUrl(backTo) : true;
        }
        // Project loaded OK. Check org mismatch.
        const org = userState.org();
        const project = projectState.project();

        if (project?.organization?.id && project.organization.id !== org?.id) {
          const currentOrgParam = state.root.queryParams[ORG_ID_PARAM];
          const urlTree = router.parseUrl(state.url);

          if (currentOrgParam && currentOrgParam !== String(project.organization.id)) {
            delete urlTree.queryParams[PROJECT_ID_PARAM];
            return urlTree;
          }

          if (!currentOrgParam) {
            urlTree.queryParams[ORG_ID_PARAM] = project.organization.id;
            return urlTree;
          }
        }

        return true;
      }),
    );
  };
};
